import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publishFunnel, unpublishFunnel, getFunnels } from "@/lib/db";
import { ExternalLink } from "lucide-react";
import type { FunnelData } from "./types";
interface PublishDialogProps {
  funnelId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isPublished: boolean;
  currentSubdomain?: string | null;
  isPro: boolean;
  onUpdate: (data: Partial<FunnelData>) => void;
}
export function PublishDialog({
  funnelId,
  isOpen,
  onOpenChange,
  isPublished,
  currentSubdomain,
  onUpdate,
  isPro
}: PublishDialogProps) {
  const [subdomain, setSubdomain] = useState(currentSubdomain || "");
  const [customDomain, setCustomDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [existingCustomDomain, setExistingCustomDomain] = useState<string | null>(null);
  const [publishedFunnelsCount, setPublishedFunnelsCount] = useState(0);
  const [customDomainInUseBy, setCustomDomainInUseBy] = useState<{
    id: string;
    name: string;
  } | null>(null);
  useEffect(() => {
    const fetchFunnelData = async () => {
      try {
        const funnels = await getFunnels();
        const currentFunnel = funnels.find(f => f.id === funnelId);
        if (currentFunnel?.customDomain) {
          setExistingCustomDomain(currentFunnel.customDomain);
          setCustomDomain(currentFunnel.customDomain);
        }

        // Check if the entered custom domain is already in use by another funnel
        if (customDomain) {
          const funnelUsingDomain = funnels.find(f => f.customDomain === customDomain && f.id !== funnelId);
          if (funnelUsingDomain) {
            setCustomDomainInUseBy({
              id: funnelUsingDomain.id,
              name: funnelUsingDomain.siteName
            });
          } else {
            setCustomDomainInUseBy(null);
          }
        }

        // Only count published non-pro funnels
        const publishedFreeFunnels = funnels.filter(f => f.isPublished && !f.isPro).length;
        setPublishedFunnelsCount(publishedFreeFunnels);
      } catch (error) {
        console.error("Error fetching funnel data:", error);
      }
    };
    if (isOpen) {
      fetchFunnelData();
    }
  }, [isOpen, funnelId, customDomain]);
  const handlePublish = async () => {
    if (!isPro && publishedFunnelsCount >= 1 && !isPublished) {
      toast.error("You can only have 1 published funnel with a free account. Upgrade to publish more!");
      return;
    }
    if (!subdomain.trim()) {
      toast.error("Please enter a subdomain first");
      return;
    }
    if (customDomain && !existingCustomDomain && !subdomain.trim()) {
      toast.error("Please enter a subdomain before adding a custom domain");
      return;
    }
    try {
      setIsLoading(true);
      await publishFunnel(funnelId, subdomain, customDomain || null);
      onUpdate({
        isPublished: true,
        subdomain,
        customDomain: customDomain || null
      });
      setExistingCustomDomain(customDomain);
      toast.success("Funnel published successfully!");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to publish funnel");
    } finally {
      setIsLoading(false);
    }
  };
  const handleUnpublish = async () => {
    try {
      setIsLoading(true);
      await unpublishFunnel(funnelId);
      onUpdate({
        isPublished: false,
        subdomain: null,
        customDomain: null
      });
      setExistingCustomDomain(null);
      setCustomDomain("");
      toast.success("Funnel unpublished successfully!");
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to unpublish funnel");
    } finally {
      setIsLoading(false);
    }
  };
  const handleRemoveCustomDomain = () => {
    setCustomDomain("");
    setExistingCustomDomain(null);
  };
  const handleOpenInNewWindow = () => {
    if (customDomain) {
      window.open(`https://${customDomain}`, '_blank');
    } else {
      window.open(`https://${currentSubdomain}.pageflo.hu`, '_blank');
    }
  };
  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isPublished ? "Manage Publication" : "Publish Funnel"}</DialogTitle>
          <DialogDescription>
            {isPublished ? "Your funnel is currently published. You can update its subdomain, custom domain, or unpublish it." : "Make your funnel accessible via a custom subdomain or domain."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="subdomain">Subdomain (kötelező)</Label>
            <div className="flex items-center space-x-2">
              <Input id="subdomain" value={subdomain} onChange={e => setSubdomain(e.target.value.toLowerCase())} placeholder="my-funnel" disabled={isLoading} />
              <span className="text-sm text-slate-500">.pageflo.hu</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customDomain">Egyedi Domain</Label>
            <div className="flex items-center space-x-2">
              <Input id="customDomain" value={customDomain} onChange={e => setCustomDomain(e.target.value.toLowerCase())} placeholder="www.mycustomdomain.com" disabled={isLoading || !subdomain.trim() || existingCustomDomain !== null} />
              {existingCustomDomain && <Button variant="destructive" size="sm" onClick={handleRemoveCustomDomain} disabled={isLoading}>Törlés</Button>}
            </div>
            {!subdomain.trim() && <p className="text-xs text-amber-500">
                Enter a subdomain first to enable custom domain
              </p>}
            {customDomainInUseBy && <p className="text-xs text-red-500">
                This domain is already in use by funnel: {customDomainInUseBy.name}
              </p>}
            {existingCustomDomain && <p className="text-xs text-amber-500v">Megváltoztatáshoz először töröld a megadott egyedi domaint.</p>}
            <p className="text-xs text-slate-500">Állítsd be a domain A rekordját a következőre: A @ 76.76.21.21</p>
          </div>

          {isPublished && (currentSubdomain || customDomain) && <Button variant="outline" onClick={handleOpenInNewWindow} className="w-full">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in New Window
            </Button>}
        </div>
        <DialogFooter>
          {isPublished ? <div className="flex space-x-2">
              <Button variant="destructive" onClick={handleUnpublish} disabled={isLoading}>Visszavonás</Button>
              <Button onClick={handlePublish} disabled={isLoading}>Beállítások frissítése</Button>
            </div> : <Button onClick={handlePublish} disabled={isLoading}>
              Publish
            </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}
;